import React from 'react';
import { Button, Tooltip } from 'antd';
import { EditOutlined, CodeOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { TEMPLATE_FORM_CONSTANTS } from '../constants';
import {
  EditorContainer,
  EditorModeButtons,
  ModeButton,
  QuillEditorWrapper,
  FormTextarea,
  FormLabel,
  AlertBox,
  AlertTitle,
  HelperText,
  FormSection,
} from '../styles';
import { getResponsiveModules } from '../utils/helpers';

interface EditorPanelProps {
  editorMode: 'visual' | 'code' | 'html';
  onModeChange: (mode: 'visual' | 'code' | 'html') => void;
  formData: any;
  onFormChange: (field: string, value: any) => void;
  quillRef: any;
  originalHtmlContent: string;
}

const formats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'align',
  'list',
  'bullet',
  'indent',
  'blockquote',
  'code-block',
  'link',
  'image',
  'script',
];

const EditorPanel: React.FC<EditorPanelProps> = ({
  editorMode,
  onModeChange,
  formData,
  onFormChange,
  quillRef,
  originalHtmlContent,
}) => {
  const modules = React.useMemo(() => getResponsiveModules(), []);

  return (
    <FormSection>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <FormLabel style={{ marginBottom: 0 }}>
          {TEMPLATE_FORM_CONSTANTS.LABELS.EMAIL_CONTENT} *
        </FormLabel>
        <EditorModeButtons>
          <Tooltip title={TEMPLATE_FORM_CONSTANTS.MESSAGES.VISUAL_EDITOR}>
            <ModeButton
              type={editorMode === 'visual' ? 'primary' : 'default'}
              size="small"
              icon={<EditOutlined />}
              onClick={() => onModeChange('visual')}
            />
          </Tooltip>
          <Tooltip title={TEMPLATE_FORM_CONSTANTS.MESSAGES.RICH_TEXT_CODE}>
            <ModeButton
              type={editorMode === 'code' ? 'primary' : 'default'}
              size="small"
              icon={<CodeOutlined />}
              onClick={() => onModeChange('code')}
            />
          </Tooltip>
          <Tooltip title="Raw HTML">
            <ModeButton
              type={editorMode === 'html' ? 'primary' : 'default'}
              size="small"
              onClick={() => onModeChange('html')}
            >
              HTML
            </ModeButton>
          </Tooltip>
        </EditorModeButtons>
      </div>

      <HelperText>{TEMPLATE_FORM_CONSTANTS.MESSAGES.HTML_REQUIRED}</HelperText>

      {editorMode === 'visual' && originalHtmlContent && (
        <AlertBox type="warning" style={{ marginTop: '12px' }}>
          <AlertTitle>⚠️ {TEMPLATE_FORM_CONSTANTS.MESSAGES.HTML_MODE_WARNING}</AlertTitle>
          <Button
            size="small"
            style={{ marginTop: '8px' }}
            onClick={() => onModeChange('html')}
          >
            {TEMPLATE_FORM_CONSTANTS.BUTTONS.RESTORE_ORIGINAL}
          </Button>
        </AlertBox>
      )}

      {editorMode === 'html' && (
        <AlertBox type="info" style={{ marginTop: '12px' }}>
          <AlertTitle>💡 <strong>HTML Mode:</strong> {TEMPLATE_FORM_CONSTANTS.MESSAGES.HTML_MODE_INFO}</AlertTitle>
        </AlertBox>
      )}

      <EditorContainer style={{ marginTop: '12px' }}>
        {editorMode === 'visual' ? (
          <QuillEditorWrapper>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formData.htmlContent}
              onChange={(content) => onFormChange('htmlContent', content)}
              modules={modules}
              formats={formats}
              style={{ height: '400px' }}
              placeholder={TEMPLATE_FORM_CONSTANTS.PLACEHOLDERS.HTML_CONTENT_VISUAL}
            />
          </QuillEditorWrapper>
        ) : editorMode === 'code' ? (
          <FormTextarea
            id="htmlContentCode"
            value={formData.htmlContent}
            onChange={(e) => onFormChange('htmlContent', e.target.value)}
            placeholder={TEMPLATE_FORM_CONSTANTS.PLACEHOLDERS.HTML_CONTENT_CODE}
            style={{ fontFamily: 'monospace', fontSize: '12px', minHeight: '450px', border: 'none' }}
          />
        ) : (
          <FormTextarea
            value={formData.htmlContent}
            onChange={(e) => onFormChange('htmlContent', e.target.value)}
            placeholder={TEMPLATE_FORM_CONSTANTS.PLACEHOLDERS.HTML_CONTENT_RAW}
            style={{ fontFamily: 'monospace', fontSize: '12px', minHeight: '450px', border: 'none' }}
          />
        )}
      </EditorContainer>
    </FormSection>
  );
};

export default EditorPanel;
